import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  LayoutDashboard, Truck, PlusCircle, User, Package, LogOut, MessageSquare,
  Navigation, Bell, Wallet, AlertTriangle, FileText, Calendar, Settings, X,
  Shield, Folder, Star, ChevronDown, ClipboardCheck, PanelLeftClose, PanelLeft,
  BookOpen
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useClientPermissions } from '../hooks/useClientPermissions';
import { useUnreadMessagesCount } from '../hooks/useUnreadMessagesCount';
import { cn } from '../lib/utils';

/**
 * Sidebar — Refonte UX 07/05/2026 (inspirée maquette client)
 * Fond blanc, items slate, état actif emerald (background + text + border-left)
 * + Mode collapsé (icônes seules)
 */

const SidebarContext = React.createContext({ collapsed: false });

const SidebarSection = ({ label, children, defaultOpen = true }) => {
  const { collapsed } = React.useContext(SidebarContext);
  const storageKey = `baobab_sidebar_section_${label.toLowerCase().replace(/\s/g, '_')}_v2`;
  const [open, setOpen] = useState(() => {
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved !== null) return saved === 'true';
    } catch {}
    return defaultOpen;
  });
  const hasActiveChild = React.Children.toArray(children).some(
    (child) => child?.props?.active
  );

  const toggle = () => {
    const next = !open;
    setOpen(next);
    try { localStorage.setItem(storageKey, String(next)); } catch {}
  };

  if (collapsed) {
    // En mode collapsé, on n'affiche pas le label de section
    return <div className="mb-1">{children}</div>;
  }

  return (
    <div className="mb-1">
      <button
        onClick={toggle}
        className="flex items-center justify-between w-full px-4 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-400 hover:text-slate-600 transition-colors"
        data-testid={`sidebar-section-${label.toLowerCase().replace(/\s/g, '-')}`}
      >
        {label}
        <ChevronDown className={cn('w-3 h-3 transition-transform duration-200', open && 'rotate-180')} />
      </button>
      <div className={cn(
        'overflow-hidden transition-all duration-200',
        open || hasActiveChild ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
      )}>
        {children}
      </div>
    </div>
  );
};

const SidebarLink = ({ icon: Icon, label, path, active, onMobileClose, badge }) => {
  const { collapsed } = React.useContext(SidebarContext);
  return (
    <Link
      to={path}
      onClick={onMobileClose}
      title={collapsed ? label : undefined}
      className={cn(
        'relative flex items-center gap-3 px-4 py-2 mx-2 rounded-lg transition-all duration-200 group',
        collapsed && 'lg:justify-center lg:px-2',
        active
          ? 'bg-emerald-50 text-emerald-700 font-semibold'
          : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
      )}
      data-testid={`sidebar-link-${path.replace(/\//g, '-').slice(1)}`}
    >
      {active && (
        <span className="absolute left-0 top-1.5 bottom-1.5 w-1 rounded-r bg-emerald-600" />
      )}
      <Icon className={cn('w-4 h-4 flex-shrink-0', active ? 'text-emerald-600' : 'text-slate-500 group-hover:text-slate-700')} />
      {!collapsed && <span className="font-medium text-sm flex-1 truncate">{label}</span>}
      {!collapsed && badge !== undefined && badge !== null && badge !== 0 && (
        <span className="ml-auto bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-4 px-1 flex items-center justify-center">
          {badge}
        </span>
      )}
      {collapsed && badge !== undefined && badge !== null && badge !== 0 && (
        <span className="absolute top-1 right-1 bg-red-500 text-white text-[9px] font-bold rounded-full min-w-[14px] h-3.5 px-1 flex items-center justify-center">
          {badge > 9 ? '9+' : badge}
        </span>
      )}
    </Link>
  );
};

const Sidebar = ({ mobileOpen, onMobileClose }) => {
  const location = useLocation();
  const { logout } = useAuth();
  const permissions = useClientPermissions();
  const { count: unreadMessages } = useUnreadMessagesCount();
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem('client_sidebar_collapsed_v2') === '1');

  useEffect(() => {
    localStorage.setItem('client_sidebar_collapsed_v2', collapsed ? '1' : '0');
  }, [collapsed]);

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

  const sidebarContent = (
    <>
      {/* Logo */}
      <div className="px-5 py-5 border-b border-slate-100 flex items-center justify-between">
        <Link to="/dashboard" onClick={onMobileClose} className={cn("flex items-center gap-3 min-w-0", collapsed && "lg:justify-center lg:gap-0")}>
          <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 p-2 rounded-xl shadow-sm shrink-0">
            <Package className="w-5 h-5 text-white" />
          </div>
          {!collapsed && (
            <div className="min-w-0 lg:block">
              <h1 className="text-base font-bold text-slate-900 leading-tight truncate">Baobab Connect</h1>
              <p className="text-[10px] text-slate-500 font-medium uppercase tracking-wide">Espace Client</p>
            </div>
          )}
        </Link>
        <button
          onClick={onMobileClose}
          className="lg:hidden text-slate-400 hover:text-slate-700 p-1 rounded hover:bg-slate-100"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Menu */}
      <nav className="flex-1 py-3 overflow-y-auto scrollbar-hide" data-testid="sidebar-nav">
        {permissions.clientRole && !collapsed && (
          <div className="mx-3 mb-3 px-3 py-2 bg-emerald-50/60 rounded-lg border border-emerald-100" data-testid="client-role-badge">
            <div className="flex items-center gap-2 text-emerald-700">
              <Shield className="w-3.5 h-3.5" />
              <span className="text-xs font-semibold">{permissions.roleLabel}</span>
            </div>
            {permissions.isReadOnly && (
              <p className="text-[10px] text-emerald-600/80 mt-0.5 ml-5">Lecture seule</p>
            )}
          </div>
        )}

        {/* Quick action */}
        {permissions.canCreateOperations && (
          <div className="mx-3 mb-3">
            <Link
              to="/nouvelle-demande"
              onClick={onMobileClose}
              title={collapsed ? 'Nouvelle demande' : undefined}
              className={cn(
                'flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg transition-all duration-200',
                'bg-emerald-600 text-white hover:bg-emerald-700 shadow-sm hover:shadow-md font-semibold text-sm',
                collapsed && 'lg:px-2'
              )}
              data-testid="sidebar-link-nouvelle-demande"
            >
              <PlusCircle className="w-4 h-4" />
              {!collapsed && 'Nouvelle demande'}
            </Link>
          </div>
        )}

        {/* Dashboard */}
        <SidebarLink icon={LayoutDashboard} label="Tableau de bord" path="/dashboard" active={isActive('/dashboard')} onMobileClose={onMobileClose} />

        {/* Operations & Logistics */}
        <SidebarSection label="Opérations" defaultOpen={true}>
          <SidebarLink icon={Truck} label="Mes opérations" path="/operations" active={isActive('/operations')} onMobileClose={onMobileClose} />
          <SidebarLink icon={FileText} label="Mes contrats" path="/mes-contrats" active={isActive('/mes-contrats')} onMobileClose={onMobileClose} />
          <SidebarLink icon={Navigation} label="Suivi temps réel" path="/suivi" active={isActive('/suivi')} onMobileClose={onMobileClose} />
          <SidebarLink icon={Calendar} label="Calendrier" path="/calendrier" active={isActive('/calendrier')} onMobileClose={onMobileClose} />
        </SidebarSection>

        {/* Finance */}
        <SidebarSection label="Finances" defaultOpen={true}>
          {permissions.canViewFinances && (
            <SidebarLink icon={Wallet} label="Finances" path="/finances" active={isActive('/finances')} onMobileClose={onMobileClose} />
          )}
          <SidebarLink icon={Folder} label="Mes documents" path="/mes-documents" active={isActive('/mes-documents')} onMobileClose={onMobileClose} />
          <SidebarLink icon={ClipboardCheck} label="Mes bons signés" path="/mes-bons" active={isActive('/mes-bons')} onMobileClose={onMobileClose} />
          <SidebarLink icon={Star} label="Évaluations" path="/evaluations" active={isActive('/evaluations')} onMobileClose={onMobileClose} />
        </SidebarSection>

        {/* Rapports */}
        <SidebarSection label="Rapports" defaultOpen={true}>
          <SidebarLink icon={FileText} label="Mes rapports" path="/mes-rapports" active={isActive('/mes-rapports')} onMobileClose={onMobileClose} />
          <SidebarLink icon={Settings} label="Config. rapports" path="/mes-rapports/configuration" active={isActive('/mes-rapports/configuration')} onMobileClose={onMobileClose} />
        </SidebarSection>

        {/* Communication & Alerts */}
        <SidebarSection label="Communication" defaultOpen={true}>
          {permissions.canViewMessaging && (
            <SidebarLink icon={MessageSquare} label="Messagerie" path="/messagerie" active={isActive('/messagerie')} onMobileClose={onMobileClose} badge={unreadMessages} />
          )}
          <SidebarLink icon={Bell} label="Notifications" path="/mes-notifications" active={isActive('/mes-notifications') || isActive('/notifications')} onMobileClose={onMobileClose} />
          <SidebarLink icon={AlertTriangle} label="Incidents" path="/mes-incidents" active={isActive('/mes-incidents')} onMobileClose={onMobileClose} />
        </SidebarSection>

        {/* Account & Settings */}
        <SidebarSection label="Paramètres" defaultOpen={true}>
          <SidebarLink icon={User} label="Mon compte" path="/mon-compte" active={isActive('/mon-compte')} onMobileClose={onMobileClose} />
        </SidebarSection>
      </nav>

      {/* Logout */}
      <div className="p-3 border-t border-slate-100 space-y-1">
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="hidden lg:flex items-center gap-3 px-4 py-2 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-700 transition-all w-full text-xs font-medium"
          data-testid="sidebar-toggle-collapse"
        >
          {collapsed ? <PanelLeft className="w-4 h-4" /> : <PanelLeftClose className="w-4 h-4" />}
          {!collapsed && <span>Réduire</span>}
        </button>
        <a
          href="/docs/client"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-3 px-4 py-2.5 rounded-lg text-slate-600 hover:bg-emerald-50 hover:text-emerald-700 transition-all duration-200 w-full group font-medium text-sm"
          data-testid="sidebar-guide-link"
          title="Guide d'utilisation Baobab Connect"
        >
          <BookOpen className="w-4 h-4 text-slate-500 group-hover:text-emerald-700 flex-shrink-0" />
          {!collapsed && <span>Guide utilisateur</span>}
        </a>
        <button
          onClick={logout}
          className="flex items-center gap-3 px-4 py-2.5 rounded-lg text-slate-600 hover:bg-red-50 hover:text-red-600 transition-all duration-200 w-full group font-medium text-sm"
          data-testid="sidebar-logout-btn"
        >
          <LogOut className="w-4 h-4 text-slate-500 group-hover:text-red-600 flex-shrink-0" />
          {!collapsed && <span>Déconnexion</span>}
        </button>
      </div>
    </>
  );

  // ----- Mobile UX: ESC close, body scroll lock, swipe-to-close -----
  useEffect(() => {
    if (!mobileOpen) return;
    const handleKey = (e) => { if (e.key === 'Escape') onMobileClose(); };
    document.addEventListener('keydown', handleKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [mobileOpen, onMobileClose]);

  const touchStartX = React.useRef(null);
  const handleTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
  const handleTouchMove = (e) => {
    if (touchStartX.current == null) return;
    const dx = e.touches[0].clientX - touchStartX.current;
    if (dx < -80) {
      onMobileClose();
      touchStartX.current = null;
    }
  };
  const handleTouchEnd = () => { touchStartX.current = null; };

  return (
    <>
      {/* Backdrop with fade-in */}
      <div
        onClick={onMobileClose}
        aria-hidden={!mobileOpen}
        className={cn(
          "fixed inset-0 bg-black/60 z-40 lg:hidden transition-opacity duration-200",
          mobileOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        )}
      />
      <aside
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        aria-hidden={!mobileOpen ? undefined : false}
        className={cn(
          "bg-white border-r border-slate-200 h-screen fixed left-0 top-0 flex flex-col shadow-xl z-50",
          "transition-transform duration-300 ease-out",
          collapsed ? "lg:w-16" : "lg:w-64",
          "w-[min(280px,85vw)]",
          mobileOpen ? "translate-x-0" : "-translate-x-full",
          "lg:translate-x-0"
        )}
      >
        <div className={cn(collapsed && "lg:[&_aside_*]:transition-all", "h-full flex flex-col")}>
          <SidebarContext.Provider value={{ collapsed }}>
            {sidebarContent}
          </SidebarContext.Provider>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
